import type { NavbarItem } from '@/shared/components/layout/Sidebar/Sidebar.types';
import { DASHBOARD_TABS, MANAGEMENT_SECTIONS } from '@/shared/config/headerTabs';

// Dashboard tabs are children of the DASHBOARD program group, but their
// backend-authorized order doesn't necessarily match the tab order Header.tsx
// displays them in - checking this order first keeps "first available" in
// sync with whichever tab a user would actually see leftmost.
const DASHBOARD_TAB_ORDER = DASHBOARD_TABS.map((tab) => tab.grant);

// Same top-level grouping/order Sidebar.tsx uses to lay out the menu, so
// "first available page" lands on whichever section would appear first there.
const PROGRAM_ORDER = [
  'DASHBOARD',
  'SYSTEM',
  'ASSET',
  'SITE-MANAGEMENT',
  'STATISTICS',
  'SCENARIO',
  'LOG',
];

const byProgramOrder = (navbarList: NavbarItem[]): NavbarItem[] =>
  [...navbarList].sort((a, b) => {
    const indexA = PROGRAM_ORDER.indexOf(a.program_id);
    const indexB = PROGRAM_ORDER.indexOf(b.program_id);
    return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
  });

/** A section's own url when it has one; group rows (SYSTEM/ASSET) leave it
 *  blank and are only routable through their children, so fall back to the
 *  first child url that is actually set. */
const sectionUrl = (item: NavbarItem): string | null =>
  item.url || item.urls?.find((u): u is string => !!u) || null;

/** First page a user with the given navbar/authority list can actually reach,
 *  or null if the list grants no route at all (e.g. empty or unloaded). */
export const getFirstAccessibleRoute = (navbarList?: NavbarItem[] | null): string | null => {
  if (!navbarList || navbarList.length === 0) return null;

  const dashboardUrls = new Set(
    navbarList
      .find((item) => item.program_id === 'DASHBOARD')
      ?.urls?.filter((url): url is string => !!url) ?? []
  );
  const firstDashboardTab = DASHBOARD_TAB_ORDER.find((url) => dashboardUrls.has(url));
  if (firstDashboardTab) return firstDashboardTab;

  for (const item of byProgramOrder(navbarList)) {
    const url = sectionUrl(item);
    if (url) return url;
  }

  return null;
};

/** First management (non-dashboard) page a user with the given
 *  navbar/authority list can reach, in the order Sidebar.tsx lays those
 *  sections out, or null when the list grants no management route. */
export const getFirstManagementRoute = (navbarList?: NavbarItem[] | null): string | null => {
  if (!navbarList || navbarList.length === 0) return null;

  for (const item of byProgramOrder(navbarList)) {
    if (item.program_id === 'DASHBOARD') continue;
    const url = sectionUrl(item);
    if (url) return url;
  }

  return null;
};

/** Where the guard below sends a path the authority list does not cover. */
export const UNAUTHORIZED_ROUTE = '/management/unauthorized';

/** The path itself or anything nested under it. */
const ownsPath = (path: string, prefix: string): boolean =>
  path === prefix || path.startsWith(`${prefix}/`);

/** Whether the authority list covers the tab/section that owns `pathname`.
 *  Routes no tab owns (e.g. /dashboard/notif-dashboard) and a list that has
 *  not loaded yet pass: this decides navigation only, the backend still
 *  authorizes every endpoint behind a page. */
export const isRouteAuthorized = (pathname: string, navbarList?: NavbarItem[] | null): boolean => {
  // The guard's own target, or redirecting there would redirect again.
  if (!navbarList || ownsPath(pathname, UNAUTHORIZED_ROUTE)) return true;

  const grantedUrls = navbarList.flatMap((item) =>
    [item.url, ...(item.urls ?? [])].filter((url): url is string => !!url)
  );

  const dashboardTab = DASHBOARD_TABS.find((tab) =>
    tab.paths.some((path) => ownsPath(pathname, path))
  );
  if (dashboardTab) return grantedUrls.includes(dashboardTab.grant);

  const section = MANAGEMENT_SECTIONS.find((prefix) => ownsPath(pathname, prefix));
  if (section) return grantedUrls.some((url) => ownsPath(url, section));

  return true;
};
